export class PdfResourceError extends Error {
  constructor(message, url) {
    super(message)
    this.name = "PdfResourceError"
    this.resourceUrl = url
  }
}

// One instance per document: cancellation and failures never cross viewers.
export async function createPdfResources({ url, version, signal, onError, timeoutMs = 10000 }) {
  let reported = false
  function failure(message, resourceUrl) {
    const error = new PdfResourceError(message, resourceUrl)
    if (!reported && !signal.aborted) {
      reported = true
      onError(error)
    }
    return error
  }

  const descriptorUrl = new URL(url, window.location.href)
  if (descriptorUrl.origin !== window.location.origin) {
    throw failure("PDF resource descriptor must be local", descriptorUrl.href)
  }

  async function read(resourceUrl, decode) {
    signal.throwIfAborted()
    const request = new AbortController()
    const cancel = () => request.abort(signal.reason)
    signal.addEventListener("abort", cancel, { once: true })
    const timer = setTimeout(() => request.abort(), timeoutMs)
    try {
      const response = await fetch(resourceUrl, { signal: request.signal, credentials: "same-origin" })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const result = await decode(response)
      signal.throwIfAborted()
      return result
    } catch (error) {
      if (signal.aborted) throw signal.reason
      throw failure(`Unable to load PDF resource: ${error.message}`, resourceUrl)
    } finally {
      clearTimeout(timer)
      signal.removeEventListener("abort", cancel)
    }
  }

  const descriptor = await read(descriptorUrl.href, response => response.json())
  if (descriptor?.version !== version ||
      !/^pdfjs-[a-f0-9]{64}\.digested$/.test(descriptor?.directory) ||
      !descriptor.cmaps || typeof descriptor.cmaps !== "object" || Array.isArray(descriptor.cmaps) ||
      !Object.hasOwn(descriptor.cmaps, "LICENSE") ||
      !Object.keys(descriptor.cmaps).some(name => name.endsWith(".bcmap")) ||
      Object.entries(descriptor.cmaps).some(([name, hash]) =>
        !/^(?:[A-Za-z0-9_-]+\.bcmap|LICENSE)$/.test(name) || !/^[a-f0-9]{64}$/.test(hash))) {
    throw failure("PDF resource descriptor does not match the viewer", descriptorUrl.href)
  }
  const baseUrl = new URL(`${descriptor.directory}/cmaps/`, descriptorUrl)

  return {
    cMapPacked: true,
    useWorkerFetch: false,
    CMapReaderFactory: class {
      async fetch({ name }) {
        const filename = `${name}.bcmap`
        if (!/^[A-Za-z0-9_-]+$/.test(name) || !Object.hasOwn(descriptor.cmaps, filename)) {
          throw failure("Unknown PDF CMap resource", baseUrl.href)
        }
        const resourceUrl = new URL(filename, baseUrl).href
        const cMapData = await read(resourceUrl, async response => {
          const bytes = new Uint8Array(await response.arrayBuffer())
          if (!bytes.length) throw new Error("Empty CMap")
          const digest = new Uint8Array(await crypto.subtle.digest("SHA-256", bytes))
          const hash = Array.from(digest, value => value.toString(16).padStart(2, "0")).join("")
          if (hash !== descriptor.cmaps[filename]) throw new Error("CMap integrity mismatch")
          return bytes
        })
        return { cMapData, isCompressed: true }
      }
    }
  }
}
